/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { MdLocationOn } from 'react-icons/md';

import PropTypes from 'prop-types';

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
} from '@material-ui/core';

import CustomDialogTitle from '~/components/CustomDialogTitle';
import LocaleMessage from '~/components/LocaleMessage';

import {
    DistributorHint,
    DistributorRoot,
    MapStage,
    MapViewport,
    Pin,
} from './styles';

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function toPercent(coord) {
    return (Number(coord) / 1000) * 100;
}

export default function MapPointDistributor({
    open,
    onClose,
    imageUrl,
    points,
    onSave,
    saving,
}) {
    const mapRef = useRef(null);
    const [pins, setPins] = useState([]);
    const [dragId, setDragId] = useState(null);

    useEffect(() => {
        if (!open) {
            setDragId(null);
            return;
        }

        const spaced = (points || []).map((point, index) => {
            const hasPosition =
                point.x !== undefined &&
                point.x !== null &&
                point.y !== undefined &&
                point.y !== null;
            const fallbackX = 120 + (index % 8) * 100;
            const fallbackY = 120 + Math.floor(index / 8) * 100;
            return {
                id: point.id,
                label: point.label || point.name || point.reference || '',
                x: hasPosition ? Number(point.x) : fallbackX,
                y: hasPosition ? Number(point.y) : fallbackY,
            };
        });
        setPins(spaced);
    }, [open, points]);

    function getNormalizedCoords(clientX, clientY) {
        if (!mapRef.current) {
            return null;
        }
        const rect = mapRef.current.getBoundingClientRect();
        if (!rect.width || !rect.height) {
            return null;
        }
        return {
            x: clamp(((clientX - rect.left) / rect.width) * 1000, 0, 1000),
            y: clamp(((clientY - rect.top) / rect.height) * 1000, 0, 1000),
        };
    }

    function movePin(id, clientX, clientY) {
        const coords = getNormalizedCoords(clientX, clientY);
        if (!coords) {
            return;
        }
        setPins(prev =>
            prev.map(pin =>
                pin.id === id ? { ...pin, x: coords.x, y: coords.y } : pin
            )
        );
    }

    function handlePointerDown(event, id) {
        event.preventDefault();
        event.stopPropagation();
        setDragId(id);
        if (event.currentTarget.setPointerCapture) {
            event.currentTarget.setPointerCapture(event.pointerId);
        }
        movePin(id, event.clientX, event.clientY);
    }

    function handlePointerMove(event) {
        if (!dragId) {
            return;
        }
        movePin(dragId, event.clientX, event.clientY);
    }

    function handlePointerUp(event) {
        if (!dragId) {
            return;
        }
        movePin(dragId, event.clientX, event.clientY);
        setDragId(null);
    }

    function handleSave() {
        onSave(
            pins.map(pin => ({
                id: pin.id,
                position: {
                    x: Math.round(pin.x),
                    y: Math.round(pin.y),
                },
            }))
        );
    }

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            maxWidth="lg"
            fullWidth
        >
            <CustomDialogTitle
                title={
                    <LocaleMessage msg="page.navmaps.layer.distribute.title" />
                }
            />
            <DialogContent dividers>
                <DistributorRoot
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                >
                    <DistributorHint>
                        <p className="hint-text">
                            <LocaleMessage msg="page.navmaps.layer.distribute.hint" />
                        </p>
                        <span className="hint-count">
                            {pins.length}{' '}
                            <LocaleMessage msg="page.navmaps.point.list.title" />
                        </span>
                    </DistributorHint>
                    <MapViewport>
                        <MapStage ref={mapRef}>
                            <img src={imageUrl} alt="" draggable={false} />
                            {pins.map(pin => (
                                <Pin
                                    key={pin.id}
                                    type="button"
                                    $x={toPercent(pin.x)}
                                    $y={toPercent(pin.y)}
                                    $dragging={dragId === pin.id}
                                    onPointerDown={event =>
                                        handlePointerDown(event, pin.id)
                                    }
                                >
                                    <span
                                        className="pin-label"
                                        title={pin.label}
                                    >
                                        {pin.label}
                                    </span>
                                    <MdLocationOn
                                        className="pin-icon"
                                        size={48}
                                    />
                                </Pin>
                            ))}
                        </MapStage>
                    </MapViewport>
                </DistributorRoot>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" disabled={saving}>
                    <LocaleMessage msg="button.cancel" />
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    autoFocus
                    disabled={saving || !pins.length}
                >
                    <LocaleMessage msg="button.save" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}

MapPointDistributor.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    imageUrl: PropTypes.string,
    points: PropTypes.array,
    onSave: PropTypes.func.isRequired,
    saving: PropTypes.bool,
};

MapPointDistributor.defaultProps = {
    open: false,
    imageUrl: '',
    points: [],
    saving: false,
};
